'use client';

import type { ReactNode } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { cn } from '@/lib/utils';
import { primaryNavActionClassName } from './primary-nav-action';

export type PrimaryNavActionProps = {
  id?: string;
  label: string;
  icon: ReactNode;
  selected?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  className?: string;
  hideLabel?: boolean;
  badgeCount?: number;
  badgeTestId?: string;
};

function NavCountBadge({ count, testId = 'primary-nav-badge' }: { count: number; testId?: string }) {
  return (
    <span
      data-testid={testId}
      className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function PrimaryNavAction({
  id,
  label,
  icon,
  selected = false,
  onClick,
  ariaLabel,
  className,
  hideLabel = false,
  badgeCount,
  badgeTestId,
}: PrimaryNavActionProps) {
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;
  return (
    <span className="relative inline-flex">
      <Button
        id={id}
        type="button"
        style="text"
        color="light"
        background="brand"
        size="sm"
        iconOnly={hideLabel}
        aria-label={ariaLabel ?? label}
        aria-current={selected ? 'page' : undefined}
        className={cn(primaryNavActionClassName(selected), className)}
        leadingIcon={icon}
        onClick={onClick}
      >
        {hideLabel ? undefined : label}
      </Button>
      {showBadge ? <NavCountBadge count={badgeCount} testId={badgeTestId} /> : null}
    </span>
  );
}

export type PrimaryNavExternalActionProps = {
  id?: string;
  label: string;
  icon: ReactNode;
  href: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  ariaLabel?: string;
  className?: string;
};

export function PrimaryNavExternalAction({
  id,
  label,
  icon,
  href,
  onClick,
  ariaLabel,
  className,
}: PrimaryNavExternalActionProps) {
  return (
    <a
      id={id}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel ?? label}
      onClick={onClick}
      className={cn(
        primaryNavActionClassName(false),
        'inline-flex items-center gap-1',
        className,
      )}
    >
      <span className="inline-flex size-4 shrink-0 items-center justify-center">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </a>
  );
}
